import React from 'react';
import classnames from 'classnames';
import styles from './FloDropdown.module.css';
import Select, { InputActionMeta } from 'react-select';
import { FloComponentProps } from 'components';

export enum FloDropdownType {
  DEFAULT
}

export interface OptionMetaBase {
  [key: string]: any;
}

export type FloDropdownOptions<T extends OptionMetaBase = {}> = FloDropdownOption<T>[];

export type FloDropdownOption<OptionMeta extends OptionMetaBase = {}> = {
  value:     string;
  label:     string;
} & OptionMeta;

export interface FloDropdownProps extends FloComponentProps {
  id:                     string,
  title:                  string;
  full?:                  boolean;
  type?:                  FloDropdownType;
  options:                FloDropdownOptions;
  onChange:               (e: any) => void;
  values:                 string | string[] | null;
  multiple?:              boolean;
  isInvalid?:             boolean;
  disabled?:              boolean;
  isLoading?:             boolean;
  loadingMessage?:        (obj: { inputValue: string }) => string | null;
  page?:                  number;
  hasMore?:               boolean;
  onLoadOptions?:         (search: string | undefined, page: number) => void;
  inputValue?:            string;
  onInputSearch?:         (newValue: string) => void;
  disableMemoryFilter?:   boolean;
}

type FloDropdownState = {
  search: string | undefined;
}

export class FloDropdown extends React.PureComponent<FloDropdownProps, FloDropdownState> {
  constructor(props: FloDropdownProps) {
    super(props);
    this.handleMenuScrollToBottom = this.handleMenuScrollToBottom.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.state = {
      search: undefined,
    }
  }

  static getDerivedStateFromProps(props: FloDropdownProps) {
    return props.inputValue !== undefined ? {
      search: props.inputValue
    } : null;
  }

  handleMenuScrollToBottom() {
    const { hasMore, onLoadOptions, page } = this.props;
    const { search } = this.state;
    if (hasMore && onLoadOptions && page) {
      onLoadOptions(search, page + 1);
    }
  }

  handleInputChange(newValue: string, actionMeta: InputActionMeta) {
    const { onLoadOptions, onInputSearch } = this.props;
    if (actionMeta.action === 'input-change') {
      if (onLoadOptions) {
        this.setState({ search: newValue });
        onLoadOptions(newValue, 1);
      }
      if (onInputSearch) {
        onInputSearch(newValue);
      }
    }
  }

  render() {
    const classes = classnames(
      styles.root,
      this.props.className,
      { [styles.full]: this.props.full },
      { [styles.isInvalid]: this.props.isInvalid },
    );

    const customStyles = {
      option: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => {

        return {
          ...styles,
          backgroundColor: isDisabled
            ? null
            : isSelected
            ? '#FFFFFF'
            : isFocused
            ? '#F9FAFE'
            : null,
          color: isDisabled
            ? '#ccc'
            : isSelected
            ? '#C93F13' : '#131E26',
          cursor: isDisabled ? 'not-allowed' : 'default',

          ':active': {
            ...styles[':active'],
            backgroundColor: !isDisabled && (isSelected ? '#F9FAFE' : '#b2e1ee'),
            color: isSelected ? '#C93F13' : '#FFF'
          },
        };
      },
    }

    const valueArr = this.props.values === null ? [] : typeof this.props.values === 'string' ? [ this.props.values ] : this.props.values;
    const value = this.props.values ? valueArr.map(val => {
      const label = this.props.options.find(op => op.value === val)?.label
      return { value: val, label };
     }) : null;
    return (
      <Select
        className             = {classes}
        classNamePrefix       = "flo-dropdown"
        value                 = { value }
        styles                = {customStyles}
        options               = {this.props.options}
        placeholder           = {this.props.title}
        onChange              = {this.props.onChange}
        isMulti               = {this.props.multiple}
        isDisabled            = {this.props.disabled}
        isLoading             = {this.props.isLoading}
        loadingMessage        = {this.props.loadingMessage}
        onMenuScrollToBottom  = {this.handleMenuScrollToBottom}
        onInputChange         = {this.handleInputChange}
        inputValue            = {this.props.inputValue}
        filterOption          = {this.props.disableMemoryFilter ? () => true : undefined}
      />
    );
  }
}

export default FloDropdown;
